import { ChangeEvent, FC, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// Types
import TimerTypes from 'types/TimerTypes';
import MediaFormats from 'types/MediaFormats';
// Components
import { Input, Select } from 'components/Controls';
import DatePicker from 'components/DatePicker';
import ImageUpload from 'components/ImageUpload';
import MultiLanguageTextField from 'components/MultiLanguageTextField';
// Utilities
import { isRequired } from 'utilities/validation';

type Props = {
  timerRef: string;
}

const TimerForm: FC<Props> = ({ timerRef }) => {
  const { t } = useTranslation();

  const { control, watch, setValue, formState: { errors } } = useFormContext();

  const watchedTimers = watch('timers');
  const timerIndex = watchedTimers.findIndex((timer: any) => timer.ref === timerRef);
  const name = `timers.${timerIndex}`;
  const watchedTimerType = watch(`${name}.type`);

  const getTimerError = useCallback((field: string) => {
    if (!errors || !errors.timers) return '';
    const timer = (errors as any).timers[timerIndex];
    return timer && timer[field] ? timer[field].message : '';
    // eslint-disable-next-line
  }, [errors]);

  return (
    <div className="flex flex-wrap -mx-6 -y-4 px-6 pb-3">
      <div className="w-full landscape:w-1/2 tablet:w-full laptop:w-7/12 px-6">
        <div className="">
          <Controller
            control={control} name={`${name}.type`}
            render={({ field: { value, onChange } }) => (
              <Select
                value={value}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  setValue(`${name}.time`, '');
                  onChange(e);
                }}
                label={t('labels.type')} id={`${name}.type`} name={`${name}.type`}
                options={typeOptions}
              />
            )}
          />
          <div className="mb-4">
            {watchedTimerType === TimerTypes.Relative && (
              <Controller
                control={control} name={`${name}.time`}
                rules={{ required: isRequired }}
                render={({ field: { onChange, value } }) => (
                  <Input
                    type="number"
                    label={t('pages.quizForm.relativeTimeLabel')} id={`${name}.time`} name={`${name}.time`}
                    value={value}
                    onChange={onChange}
                    required={true}
                    error={Boolean(getTimerError('time'))}
                    errorText={getTimerError('time')}
                    helperText={t('pages.quizForm.relativeTimeHint')}
                  />
                )}
              />
            )}
            {watchedTimerType === TimerTypes.Absolute && (
              <Controller
                control={control} name={`${name}.time`}
                rules={{ required: isRequired }}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    label={t('pages.quizForm.absoluteTimeLabel')}
                    id={`${name}.time`}
                    required={true}
                    error={Boolean(getTimerError('time'))}
                    errorText={getTimerError('time')}
                    helperText={t('pages.quizForm.absoluteTimeHint')}
                  />
                )}
              />
            )}
          </div>
          <MultiLanguageTextField
            name={`${name}.title`}
            label={t('labels.title')}
            className="mb-4"
          />
          <Controller
            control={control} name={`${name}.url`}
            render={({ field }) => (
              <Input
                {...field}
                label={t('labels.imageLink')} id={`${name}.url`} name={`${name}.url`}
              />
            )}
          />
        </div>
      </div>
      <div
        className="
          w-full landscape:w-1/2 tablet:w-full laptop:w-5/12
          landscape:border-l tablet:border-l-0 laptop:border-l
          px-6
        "
        style={{ borderColor: 'rgba(255,255,255,0.2)' }}
      >
        <Controller
          control={control}
          name={`${name}.image`}
          render={({ field }) => (
            <ImageUpload
              {...field}
              format={MediaFormats.CTA}
              label="labels.image"
            />
          )}
        />
      </div>
    </div>
  );
};

export default TimerForm;

const typeOptions = (Object.keys(TimerTypes) as Array<keyof typeof TimerTypes>).map(key => ({
  value: TimerTypes[key],
  label: key,
}));
